import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  getMissionsPayload,
  listMissions,
  setMissionActive,
} from '../../services/missionsApi';
import { completionModeShort, missionChains } from './missionUtils';
import { useMissionsSession } from './useMissionsSession';

function MissionList() {
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState(null);
  const [error, setError] = useState('');
  const [missions, setMissions] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { handleApiResponse } = useMissionsSession(setError);

  const loadMissions = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const result = await listMissions();
      if (handleApiResponse(result, 'Failed to load missions.')) {
        setMissions([]);
        return;
      }
      const data = getMissionsPayload(result);
      const list = data?.missions || data || [];
      setMissions(Array.isArray(list) ? list.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)) : []);
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load missions.');
      setMissions([]);
    } finally {
      setLoading(false);
    }
  }, [handleApiResponse]);

  useEffect(() => {
    loadMissions();
  }, [loadMissions]);

  const handleToggleActive = async (mission) => {
    const nextActive = !mission.is_active;
    setTogglingId(mission.id);

    try {
      const result = await setMissionActive(mission.id, nextActive);
      if (handleApiResponse(result, 'Failed to update mission status.')) {
        return;
      }
      setMissions((prev) =>
        prev.map((m) => (m.id === mission.id ? { ...m, is_active: nextActive } : m))
      );
      setSnackbar({
        open: true,
        message: nextActive ? 'Mission activated.' : 'Mission deactivated.',
        severity: 'success',
      });
    } catch (toggleError) {
      setSnackbar({
        open: true,
        message: toggleError.message || 'Failed to update mission status.',
        severity: 'error',
      });
    } finally {
      setTogglingId(null);
    }
  };

  const missionById = useMemo(() => {
    const map = {};
    missions.forEach((m) => {
      map[m.id] = m;
    });
    return map;
  }, [missions]);

  const chains = useMemo(() => missionChains(missions), [missions]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Mission Configuration</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadMissions} disabled={loading} title="Refresh">
            <RefreshIcon />
          </IconButton>
          <Button
            component={Link}
            to="/admin/missions/new"
            variant="contained"
            startIcon={<AddIcon />}
          >
            New Mission
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        There is no delete API — deactivate instead. Replacing tasks is blocked while the mission is
        active and assigned. Admin can edit while the app <code>missions</code> feature flag is off.
      </Alert>

      {!loading && chains.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {chains.map((chain, i) => (
            <Typography key={i} variant="body2" component="div">
              Chain{chains.length > 1 ? ` ${i + 1}` : ''}: {chain.map((m) => m.name).join(' → ')} → end
            </Typography>
          ))}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Order</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Stage</TableCell>
                <TableCell>FE Reward</TableCell>
                <TableCell>Tasks</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Next</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {missions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No missions found.
                  </TableCell>
                </TableRow>
              ) : (
                missions.map((mission) => (
                  <TableRow key={mission.id} hover sx={{ opacity: mission.is_active ? 1 : 0.65 }}>
                    <TableCell>{mission.sort_order}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                        {mission.image_url ? (
                          <Box
                            component="img"
                            src={mission.image_url}
                            alt=""
                            sx={{
                              width: 40,
                              height: 40,
                              objectFit: 'cover',
                              borderRadius: 1,
                              bgcolor: '#eee',
                            }}
                          />
                        ) : null}
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {mission.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {mission.purpose}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{mission.stage}</TableCell>
                    <TableCell>{mission.reward_fe}</TableCell>
                    <TableCell>{mission.requirements?.length ?? 0}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={completionModeShort(mission.completion_mode)}
                        title={mission.completion_mode}
                      />
                    </TableCell>
                    <TableCell>
                      {mission.next_mission_id
                        ? (missionById[mission.next_mission_id]?.name || `#${mission.next_mission_id}`)
                        : '—'}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={!!mission.is_active}
                        disabled={togglingId === mission.id}
                        onChange={() => handleToggleActive(mission)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        component={Link}
                        to={`/admin/missions/${mission.id}`}
                        size="small"
                        startIcon={<EditIcon />}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}

export default MissionList;
